import PropTypes from "prop-types";
import { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
} from "@mui/material";
import { ChartOfAccountSelect } from "@/features/chart-of-accounts";
import {
  CenteredLoading,
  CustomDialogTitle,
  CustomInputField,
  CustomSelect,
} from "../../ui";
import { StatusEnum, statusLabels } from "../../shared";
import {
  COA_TYPES,
  DEFAULT_VALUES,
  VALIDATION_SCHEMA,
} from "./expense-category-form-constants";

export const ExpenseCategoryFormDialog = ({
  defaultValues,
  onSubmit,
  isOpen,
  onClose,
  dialogTitle,
  renderDialogActionsChildren,
  isLoading = false,
}) => {
  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting, errors },
  } = useForm({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(VALIDATION_SCHEMA),
  });

  useEffect(() => {
    if (defaultValues) reset({ ...DEFAULT_VALUES, ...defaultValues });
  }, [defaultValues, reset]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const onFormSubmit = async (formData) => {
    await onSubmit({
      ...formData,
      chartOfAccountId: formData.chartOfAccount?.id || null,
    });
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth>
      <CustomDialogTitle
        variant="h5"
        title={dialogTitle}
        onClose={handleClose}
      />
      {isLoading ? (
        <CenteredLoading sx={{ height: "25rem" }} />
      ) : (
        <Box component="form" onSubmit={handleSubmit(onFormSubmit)} noValidate>
          <DialogContent>
            <Stack spacing={2}>
              <CustomInputField
                required
                name="name"
                label="Nama"
                register={register}
                helperText={errors.name?.message}
                error={Boolean(errors.name)}
              />
              <Controller
                control={control}
                name="status"
                render={({ field, fieldState }) => (
                  <CustomSelect
                    required
                    value={field.value}
                    onChange={field.onChange}
                    id="status"
                    label="Status"
                    options={Object.values(StatusEnum).map((value) => ({
                      value: value,
                      label: statusLabels[value],
                    }))}
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
              <Controller
                control={control}
                name="chartOfAccount"
                render={({ field, fieldState }) => (
                  <ChartOfAccountSelect
                    value={field.value}
                    onChange={field.onChange}
                    types={COA_TYPES}
                    TextFieldProps={{
                      required: true,
                      label: "Akun",
                      error: fieldState.invalid,
                      helperText: fieldState.error?.message,
                      InputProps: {
                        placeholder: "Pilih Akun",
                      },
                    }}
                  />
                )}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            {renderDialogActionsChildren({ isLoading: isSubmitting })}
          </DialogActions>
        </Box>
      )}
    </Dialog>
  );
};

ExpenseCategoryFormDialog.propTypes = {
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  dialogTitle: PropTypes.string.isRequired,
  renderDialogActionsChildren: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};
